import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useReactToPrint } from "react-to-print";
import { HandIcon } from "lucide-react";

import {
  cn,
  getText,
  interpolateString,
  titleCase,
  type Noticeboard,
  type NoticeboardResult,
} from "shared/lib";
import {
  conversationOverrideVar,
  languageVar,
  messagesVar,
  noticeboardDataVar,
} from "@/variables/globalVar";
import { EventTypes } from "@/types/events";

import { useGetPublicConversation } from "shared/apiHooks";
import {
  useInitMultiLingual,
  useMediaQuery,
  useTranslateMessages,
  useTranslateNoticeboard,
} from "shared/hooks";
import {
  useAccount,
  useInitGoogleMaps,
  useShouldRenderSideBySide,
} from "@/hooks";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "shared/components";
import { Portal } from "@/components/Portal";
import { LocalisedLogo } from "@/components/LocalisedLogo";
import { AnalysisLayoutContainer } from "@/components/analysisLayout";
import { AnalysisTabsContainer } from "@/components/analysisLayout/analysisTabs";
import { PrintView } from "@/components/printView";
import { LanguageDropdown } from "@/components/header/LanguageDropdown";
import { PostSentToCouncilHelpMessage } from "@/components/helpMessage";
import { PublicViewHeader } from "./PublicViewHeader";

const API_BASE_URL = import.meta.env.VITE_REACT_APP_BACKEND_ENDPOINT;

export function PublicView() {
  const [searchParams] = useSearchParams();
  const identifier = searchParams.get("id") || "";

  useInitGoogleMaps();

  const language = useAtomValue(languageVar);

  useInitMultiLingual(language, API_BASE_URL);

  const md = useMediaQuery({
    query: "(min-width: 768px)",
  });

  const shouldRenderSideBySide = useShouldRenderSideBySide();

  const componentRef = useRef(null);

  const [noticeboardData, setNoticeboardDataVar] = useAtom(noticeboardDataVar);
  const [messages, setMessagesVar] = useAtom(messagesVar);

  const [isValid, setIsValid] = useState(false);

  const setConversationOverride = useSetAtom(conversationOverrideVar);

  const { conversation } = useAccount();

  const { data: publicConversationResponse } = useGetPublicConversation({
    path: { conversation_identifier: identifier.toLowerCase() },
  });

  useEffect(() => {
    const reRenderPortalsEvent = new CustomEvent(EventTypes.ReRenderPortals);
    document.dispatchEvent(reRenderPortalsEvent);
  }, [shouldRenderSideBySide]);

  useEffect(() => {
    if (publicConversationResponse?.data) {
      console.log(
        "<PublicViewPage> publicConversationResponse",
        publicConversationResponse.data,
      );

      setConversationOverride(publicConversationResponse.data);
      setMessagesVar(publicConversationResponse.data.messages || []);

      const noticeboardArray = publicConversationResponse.data
        .noticeboard as unknown as Array<NoticeboardResult>;
      setNoticeboardDataVar(
        noticeboardArray.reduce((noticeboard, result) => {
          noticeboard[result.expert_name] = result;
          return noticeboard;
        }, {} as Noticeboard),
      );

      setIsValid(true);
    }
  }, [publicConversationResponse]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    console.log("<PublicViewPage> noticeboardData", noticeboardData);
  }, [noticeboardData]);

  useEffect(() => {
    if (conversation?.address) {
      document.title = interpolateString(
        "Planning Enquiry Report for {{address}}",
        {
          address: titleCase(conversation.address),
        },
      );
    }
  }, [conversation]);

  const showTranslated = language !== "en";

  const transformedNoticeboard = useTranslateNoticeboard(
    noticeboardData,
    showTranslated,
  );

  const transformedMessages = useTranslateMessages(messages, showTranslated);

  const print = useReactToPrint({
    content: () => componentRef.current,
    removeAfterPrint: true,
    documentTitle: getText("MyLot Report"),
    onBeforeGetContent: () => {},
    onPrintError: () => alert("There was an error during printing"),
  });

  useEffect(() => {
    const handlePrint: EventListener = (_event) => {
      print();
    };

    document.addEventListener(EventTypes.PrintPublicReport, handlePrint);

    return () => {
      document.removeEventListener(EventTypes.PrintPublicReport, handlePrint);
    };
  }, [print]);

  const renderToolbar = () => (
    <div className="flex items-center justify-between bg-[#F3F2F0] py-3 pl-4 pr-3 md:min-h-[60px] md:py-0 md:pl-8 md:pr-9">
      <div className="flex h-[32.51px] w-[73.19px] cursor-pointer items-center justify-center md:h-[36.57px] md:w-[82.34px]">
        <LocalisedLogo />
      </div>

      <Dialog>
        <DialogTrigger
          tabIndex={0}
          className="bg-interfaceWhite border-interactiveBorder text-bodyxs flex cursor-pointer items-center justify-between gap-1 rounded border px-3 py-1.5"
        >
          <HandIcon className="h-4 w-4" />
          {getText("Help")}
        </DialogTrigger>
        <DialogContent>
          <DialogTitle>Help</DialogTitle>
          <PostSentToCouncilHelpMessage />
        </DialogContent>
      </Dialog>

      {conversation?.language && conversation.language !== "en" && (
        <LanguageDropdown whiteListOptionValues={[conversation.language]} />
      )}
    </div>
  );

  return (
    <div className="text-textPrimary bg-interfaceBgLightPaper h-dvh w-screen">
      {!isValid && <div>Loading</div>}

      {isValid && (
        <div className="flex h-full w-full flex-col overflow-hidden">
          {renderToolbar()}

          <section
            aria-label="Public report"
            className={cn(
              "flex grow flex-col overflow-auto px-5 py-5 md:pt-2",
              !md ? "bg-interfaceBgLightPaper" : "bg-interfaceBgDarkPaper",
            )}
            role="region"
          >
            <div
              className={cn(
                "flex grow flex-col rounded-xl",
                md || shouldRenderSideBySide ? "bg-interfaceBgLightPaper" : "",
              )}
            >
              <AnalysisLayoutContainer
                conversation={conversation}
                isPublic
                messages={transformedMessages}
                noticeboardData={transformedNoticeboard}
              />
            </div>
          </section>

          {!shouldRenderSideBySide ? (
            <AnalysisTabsContainer conversation={conversation} />
          ) : null}

          <Portal
            elementId={
              shouldRenderSideBySide
                ? "sidebar-header-portal"
                : "content-header-portal"
            }
          >
            <PublicViewHeader conversation={conversation} />
          </Portal>

          <div className="hidden">
            <div ref={componentRef}>
              <PrintView
                conversation={conversation}
                messages={transformedMessages}
                noticeboardData={transformedNoticeboard}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
